<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.unregister.heading",
                "Delete entry confirmation"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.unregister.explanation.text",
                "You are going to delete the following entry."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t(
                "events:entryAction.unregister.deleteFrom.label",
                "Delete from"
              )
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t("events:entryAction.unregister.athleteNames.label", "Athlete")
            }}
          </template>
          <template #horseNamesHeader>
            {{ $t("events:entryAction.unregister.horseNames.label", "Horse") }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <div class="mb-2">
          <b>{{
            $t(
              "events:entryAction.unregister.alertNote.heading",
              "Please note:"
            )
          }}</b>
        </div>

        <AppAlertNotice center>
          <template v-if="entry.connectedWithFei">
            {{
              $t(
                "events:entryAction.unregister.alertNoteFei.text",
                "Entry will be deleted in the FEI entry system."
              )
            }}
          </template>
          <template v-else>
            {{
              $t(
                "events:entryAction.unregister.alertNoteNotFei.text",
                "Entry will be deleted."
              )
            }}
          </template>

          <div v-if="hasEventProducts" class="my-2">
            {{
              $t(
                "events:entryAction.unregister.alertNoteHasRelatedProducts.text",
                "This entry has a connected product or several products that will be unregistered, if entry is unregistered."
              )
            }}
          </div>
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryAction.unregister.confirmUnregister.button",
          "Confirm delete"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed, inject } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo/index";

import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";
import { useEntryFeiActionsInjections } from "./compositions";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";

defineOptions({
  name: "EntryFeiUnregister"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const modal = ref(false);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);

const entryLoading = ref(false);
const entry = ref(null);

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    console.log("meow", v);
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
    }

    modal.value = v;
  }
});

const masterlistRefetchQueries = inject("RegistrationsRefetchQueryName", null);

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.unregister.success.heading",
          "Entry deleted"
        ),
        main: t(
          "events:entryAction.unregister.success.mainMessage.text",
          " Entry is deleted"
        ),
        additional: "",
        okBtn: t("events:entryAction.unregister.success.ok.button", "OK")
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.unregister.failed.heading",
          "Delete entry"
        ),
        main: t(
          "events:entryAction.unregister.failed.mainMessage.text",
          "Entry cannot be deleted"
        ),
        reason: t(
          "events:entryAction.unregister.failed.reason.text",
          "The FEI entry system reported the following reason:"
        ),
        additional: t(
          "events:entryAction.unregister.failed.additionalMessage.text",
          "Please contact your National Federation, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.unregister.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;

  try {
    const { data } = await apolloClient.query({
      query: gql`
          query EntryFeiUnregisterFetch($id: ID!) {
            entry(id: $id) {
              id
              connectedWithFei
              allowedMutations
              registrationEventProducts {
                id
                status
              }
              registration {
                id
                athletes {
                  id
                  name
                  ${AppEntryRegistrationOverview.fragments.personFields}
                }
                horses {
                  id
                  name
                  ${AppEntryRegistrationOverview.fragments.horseFields}
                }
                eventId
                event {
                  id
                  ${AppShowEventDetails.fragments.event}
                }
              }
              competition {
                id
                code
                ${AppEntryRegistrationOverview.fragments.competitionFields}
              }
            }
          }
        `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

const hasEventProducts = computed(
  () =>
    entry.value &&
    entry.value.registrationEventProducts.filter(x => x.status != "cancelled")
      .length > 0
);

async function submit() {
  formSubmitted.value = true;

  try {
    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entryUnregister($entryId: ID) {
          entryUnregister(input: { entryId: $entryId }) {
            entry {
              id
              status
              resolution
              allowedMutations
              registration {
                id
                entries {
                  id
                  status
                  resolution
                  allowedMutations
                  registration {
                    id
                    registrationEventProducts {
                      id
                      status
                    }
                    entries {
                      id
                      status
                      resolution
                    }
                  }
                }
              }
            }
          }
        }
      `,
      refetchQueries: [masterlistRefetchQueries].filter(Boolean),
      variables: {
        entryId: entry.value.id
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      responseType.value = "success";
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}
defineExpose({ openFeiActionForm });
</script>
